/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { t } from 'i18next';

// Component imports
import MemoizedSingleConfigField from './LLMSingleConfigField';
import { Col, Empty, Row } from 'antd';

// Misc imports

/**
 * @typedef {object} LLMDynamicModelConfigFieldsProps
 * @property {array} basePath - Required to pass down and connect the form fields with the main form and for setting up watchers
 * @property {array} modelMappings - Required to render the field inputs according to each mapping
 * @property {object} form - The main form which this component is connected to
 */

/**
 * =====================================================================================================
 *
 * CODE DESCRIPTION
 *  This is just an intermediate path to render each field for each mapping
 *
 *
 * TECHNICAL SPECIFICATIONS
 * @param {LLMDynamicModelConfigFieldsProps} props - The props for the component.
 * @returns {React.ReactElement} The rendered component.
 *
 *
 * METADATA
 * @since 2026-02-17
 * @version 1.0.0
 * =====================================================================================================
 */

const LLMDynamicModelConfigFields = ({
  basePath,
  modelMappings,
  form
}) => {

  const stableBasePath = useMemo(() => Array.isArray(basePath) ? basePath : [basePath], [basePath ? basePath.join('.') : '']);

  if (!modelMappings || modelMappings.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No configuration available for this model.')} />;
  }

  return (
    <Row>
      <Col xs={24}>
        {modelMappings.map((field) => (
          <MemoizedSingleConfigField
            key={field.id}
            field={field}
            basePath={stableBasePath}
            form={form}
          />
        ))}
      </Col>
    </Row>
  );
};

LLMDynamicModelConfigFields.propTypes = {
  basePath: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
  modelMappings: PropTypes.array.isRequired,
  form: PropTypes.object
};

export default LLMDynamicModelConfigFields;
